import classNames from "classnames";
import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { ReactComponent as ReloadIcon } from "assets/images/common/reload.svg";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import DateRangePicker, { DateRangeValue } from "components/DateRangePicker";
import SearchInput from "components/SearchInput";
import Select from "components/Select";
import Table from "components/Table";

import { HEADERS, SEVERITY_OPTIONS, STATUS_LABELS, STATUS_OPTIONS, STAT_CARDS, TYPE_OPTIONS } from "./data";

import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  getIncidentListRequest,
  getIncidentStatisticsRequest,
  resetIncidentList,
  resetIncidentStatistics,
} from "store/slices/incidentsSlice";

import useDebounce from "hooks/useDebounce";
import useRealtime from "hooks/useRealtime";
import { QUERY_PARAMS } from "utils/constants";
import { DateHelper, QueryHelper } from "utils/helpers";

import { IncidentStatistics } from "entities/common";
import { IncidentStatus } from "entities/incidents";
import { AssetsApi } from "services/http";

import styles from "./style.module.scss";

type PropTypes = {
  id: number;
};

const PortfolioIncidentsTab: FC<PropTypes> = ({ id }) => {
  const dispatch = useAppDispatch();
  const { statistics, list, meta } = useAppSelector((store) => store.incidents);

  const [showFilters, setShowFilters] = useState(true);
  const [assetLabel, setAssetLabel] = useState("");
  const [assetValue, setAssetValue] = useState("");
  const [typeValue, setTypeValue] = useState("");
  const [severityValue, setSeverityValue] = useState("");
  const [statusValue, setStatusValue] = useState("");
  const [dateRange, setDateRange] = useState<DateRangeValue>({ from: null, to: null });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const handleRequestList = useCallback((silent = false) => {
    dispatch(
      getIncidentListRequest(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.PORTFOLIO_ID]: id,
          [QUERY_PARAMS.ASSET_ID]: assetValue ? Number(assetValue) : undefined,
          [QUERY_PARAMS.SEARCH]: debouncedSearch?.trim()?.toLowerCase() || undefined,
          [QUERY_PARAMS.INCIDENT_TYPE]: typeValue || undefined,
          [QUERY_PARAMS.SEVERITY]: severityValue || undefined,
          [QUERY_PARAMS.STATUS]: statusValue || undefined,
          [QUERY_PARAMS.TIME_DETECTED_FROM]: dateRange.from || undefined,
          [QUERY_PARAMS.TIME_DETECTED_TO]: dateRange.to || undefined,
          [QUERY_PARAMS.PAGE]: page,
          [QUERY_PARAMS.PAGE_SIZE]: 20,
        }),
        silent ? { silent: true } : undefined,
      ),
    );
  }, [
    assetValue,
    dateRange.from,
    dateRange.to,
    debouncedSearch,
    dispatch,
    id,
    page,
    severityValue,
    statusValue,
    typeValue,
  ]);

  const handleStatisticsRequest = useCallback(
    (silent = false) => {
      dispatch(
        getIncidentStatisticsRequest(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.PORTFOLIO_ID]: id,
          }),
          silent ? { silent: true } : undefined,
        ),
      );
    },
    [dispatch, id],
  );

  const handleClearFilters = () => {
    setSearch("");
    setAssetValue("");
    setTypeValue("");
    setSeverityValue("");
    setStatusValue("");
    setDateRange({ from: null, to: null });
    setPage(1);
  };

  const handleAssetChange = (value: string, label: string) => {
    setAssetValue(value);
    setAssetLabel(label);
  };

  const handleAssetSearch = useCallback(
    async (search: string, page: number) => {
      try {
        const { data } = await AssetsApi.getList(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: search || undefined,
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PORTFOLIO_ID]: id,
            [QUERY_PARAMS.PAGE_SIZE]: 50,
          }),
        );

        const options = [
          { value: "", label: "All assets" },
          ...(data?.items || []).map((item) => ({ value: String(item.id), label: item.name })),
        ];
        const hasMore = data?.total ? page * 50 < data.total : false;

        return { options, hasMore };
      } catch {
        return { options: [{ value: "", label: "All assets" }], hasMore: false };
      }
    },
    [id],
  );

  const tableData = useMemo(
    () =>
      list.map((incident) => ({
        asset: <span className={styles["one-row"]}>{incident.asset}</span>,
        location: <span className={styles.bold}>{incident.location}</span>,
        type: <span className={styles.neutral}>{incident.type}</span>,
        incident: incident.incident,
        severity: (
          <span className={classNames(styles.severity, styles[`severity-${incident.severity.toLowerCase()}`])}>
            {incident.severity}
          </span>
        ),
        timeDetected: (
          <span className={styles.neutral}>
            {incident.time_detected ? DateHelper.toFormat(incident.time_detected, "dd.MM.yyyy HH:mm") : "-"}
          </span>
        ),
        duration: incident.duration,
        timeResolved: (
          <span className={styles.neutral}>
            {incident.time_resolved ? DateHelper.toFormat(incident.time_resolved, "dd.MM.yyyy HH:mm") : "-"}
          </span>
        ),
        status: (
          <span className={classNames(styles.status, styles[`status-${incident.status}`])}>
            {STATUS_LABELS[incident.status as IncidentStatus]}
          </span>
        ),
      })),
    [list],
  );

  // reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [assetValue, dateRange.from, dateRange.to, debouncedSearch, severityValue, statusValue, typeValue]);

  useEffect(() => {
    handleStatisticsRequest();
  }, [handleStatisticsRequest]);

  useEffect(() => {
    handleRequestList();
  }, [handleRequestList]);

  const reload = useCallback(() => {
    handleStatisticsRequest(true);
    handleRequestList(true);
  }, [handleStatisticsRequest, handleRequestList]);

  useRealtime(["incidents"], reload, { portfolioId: Number(id) });

  useEffect(() => {
    return () => {
      dispatch(resetIncidentList());
      dispatch(resetIncidentStatistics());
    };
  }, [dispatch]);

  return (
    <div className={styles.incidents}>
      <div className={styles.stats}>
        {STAT_CARDS.map((card) => (
          <div key={card.title} className={styles.statCard}>
            <span className={styles.statTitle}>{card.title}</span>
            <span className={styles.statValue}>{statistics[card.key as keyof IncidentStatistics]}</span>
            <span className={styles.statSubtitle}>{card.subtitle}</span>
          </div>
        ))}
      </div>

      <div className={styles.search}>
        <SearchInput
          name="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search incidents..."
          className={styles.searchInput}
        />

        <Button variant="secondary" className={styles.filtersToggle} onClick={() => setShowFilters((prev) => !prev)}>
          {showFilters ? "Close filters" : "Open filters"}
        </Button>
      </div>

      {showFilters && (
        <div className={styles.filters}>
          <div className={styles.filtersWrap}>
            <AsyncSearchableSelect
              value={assetValue}
              variant="secondary"
              placeholder="All assets"
              selectedLabel={assetLabel}
              onChange={handleAssetChange}
              onSearch={handleAssetSearch}
              className={styles.filter}
            />

            <Select
              variant="secondary"
              options={TYPE_OPTIONS}
              value={typeValue}
              onChange={setTypeValue}
              placeholder="All types"
              className={styles.filter}
            />

            <Select
              variant="secondary"
              options={SEVERITY_OPTIONS}
              value={severityValue}
              onChange={setSeverityValue}
              placeholder="All severities"
              className={styles.filter}
            />

            <Select
              variant="secondary"
              options={STATUS_OPTIONS}
              value={statusValue}
              onChange={setStatusValue}
              placeholder="All statuses"
              className={styles.filter}
            />

            <DateRangePicker value={dateRange} onChange={setDateRange} className={styles.dateRange} />
          </div>

          <Button form="circle" variant="white" className={styles.reload} onClick={handleClearFilters}>
            <ReloadIcon />
          </Button>

          <Button variant="white" className={styles.clearFilters} onClick={handleClearFilters}>
            Clear filters
          </Button>
        </div>
      )}

      <div className={styles.tableScroll}>
        <Table
          headers={HEADERS}
          data={tableData}
          pagination={{ total: Math.ceil(meta.total / meta.page_size) || 1, page, onPageChange: setPage }}
          className={styles.table}
        />
      </div>
    </div>
  );
};

export default PortfolioIncidentsTab;
